/**
 * Local theme / avatar photos when Cloudflare R2 Worker is unavailable.
 * Stores compressed JPEG data URLs in localStorage (same budget as profile photos).
 */
import {
  compressImageFileForProfilePhoto,
  LOCAL_PROFILE_PHOTO_MAX_BYTES,
} from "./localProfilePhotoStorage";

/** @typedef {'player' | 'organization' | 'competition'} MediaKind */

const STORAGE_PREFIX = "shuttlescore:themePhoto:";

const KIND_EDGE = {
  player: 512,
  organization: 720,
  competition: 1280,
};

function storageKey(kind, entityId) {
  return `${STORAGE_PREFIX}${kind}:${entityId}`;
}

/**
 * @param {MediaKind} kind
 * @param {string} entityId
 * @returns {string | null}
 */
export function getLocalEntityThemePhoto(kind, entityId) {
  if (!kind || !entityId || typeof localStorage === "undefined") return null;
  try {
    return localStorage.getItem(storageKey(kind, entityId));
  } catch {
    return null;
  }
}

/**
 * @param {MediaKind} kind
 * @param {string} entityId
 * @param {string} dataUrl
 */
export function saveLocalEntityThemePhoto(kind, entityId, dataUrl) {
  if (!kind || !entityId) throw new Error("Media id required");
  if (!dataUrl || !String(dataUrl).startsWith("data:image/")) {
    throw new Error("Invalid image data");
  }
  if (dataUrl.length > LOCAL_PROFILE_PHOTO_MAX_BYTES) {
    throw new Error("Image is too large for offline storage");
  }
  try {
    localStorage.setItem(storageKey(kind, entityId), dataUrl);
  } catch {
    throw new Error("Could not save photo offline — try a smaller image");
  }
}

/**
 * @param {MediaKind} kind
 * @param {string} entityId
 */
export function removeLocalEntityThemePhoto(kind, entityId) {
  if (!kind || !entityId || typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(storageKey(kind, entityId));
  } catch {
    /* ignore */
  }
}

/**
 * Compress + save locally; returns data URL for use as logoUrl / bannerImageUrl / photo.
 * @param {{ kind: MediaKind, entityId: string, file: File }} input
 * @returns {Promise<string>}
 */
export async function storeEntityThemePhotoLocally({ kind, entityId, file }) {
  const maxEdge = KIND_EDGE[kind] || 512;
  const dataUrl = await compressImageFileForProfilePhoto(file, {
    maxEdge,
    maxBytes: LOCAL_PROFILE_PHOTO_MAX_BYTES,
  });
  saveLocalEntityThemePhoto(kind, entityId, dataUrl);
  return dataUrl;
}

/**
 * Prefer cloud Storage; fall back to local data URL when Storage fails / unavailable.
 * @param {{
 *   kind: MediaKind,
 *   entityId: string,
 *   file: File,
 *   onProgress?: (phase: string, detail?: object) => void,
 *   uploadCloud: (args: {
 *     kind: MediaKind,
 *     entityId: string,
 *     file: File,
 *     onProgress?: (phase: string, detail?: object) => void,
 *   }) => Promise<string>,
 * }} input
 * @returns {Promise<{ url: string, source: 'cloud' | 'local' }>}
 */
export async function uploadEntityThemePhotoWithLocalFallback({
  kind,
  entityId,
  file,
  onProgress,
  uploadCloud,
}) {
  try {
    const url = await uploadCloud({ kind, entityId, file, onProgress });
    try {
      await storeEntityThemePhotoLocally({ kind, entityId, file });
    } catch {
      /* optional cache */
    }
    return { url, source: "cloud" };
  } catch (e) {
    const code = /** @type {{ code?: string }} */ (e)?.code;
    // Only soft-fallback when the Worker isn’t configured; otherwise surface the error.
    if (code !== "media_api_missing") throw e;
    onProgress?.("saving");
    const url = await storeEntityThemePhotoLocally({ kind, entityId, file });
    return { url, source: "local" };
  }
}
